import { ConfirmActionHost } from '@/components/ui/confirm-action';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { AuthProvider } from "@/contexts/auth/AuthProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import AppLayout from "@/components/AppLayout";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { lazy, Suspense } from "react";
import "./App.css";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Eagerly loaded pages (critical path)
import Index from "@/pages/Index";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import NotFound from "@/pages/NotFound";

// Lazy loaded pages (code-splitting for better performance)
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Properties = lazy(() => import("@/pages/Properties"));
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"));
const AddProperty = lazy(() => import("@/pages/AddProperty"));
const EditProperty = lazy(() => import("@/pages/EditProperty"));
const Finance = lazy(() => import("@/pages/Finance"));
const FinancialReport = lazy(() => import("@/pages/FinancialReport"));
const BRRRProjects = lazy(() => import("@/pages/BRRRProjects"));
const NewBRRRProject = lazy(() => import("@/pages/NewBRRRProject"));
const BRRRProjectDetail = lazy(() => import("@/pages/BRRRProjectDetail"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const Settings = lazy(() => import("@/pages/Settings"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const HelpCentre = lazy(() => import("@/pages/HelpCentre"));
const Glossary = lazy(() => import("@/pages/Glossary"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const UserContacts = lazy(() => import("@/pages/UserContacts"));
const Landing = lazy(() => import("@/pages/Landing"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const Landlords = lazy(() => import("@/pages/Landlords"));
const LandlordDetail = lazy(() => import("@/pages/LandlordDetail"));
const InvestmentAnalyser = lazy(() => import("@/pages/InvestmentAnalyser"));
const BRRRDealCalculatorUK = lazy(() => import("@/pages/BRRRDealCalculatorUK"));
const PropertyManagementToolForLandlords = lazy(() => import("@/pages/PropertyManagementToolForLandlords"));
const ToolsForAirbnbInvestors = lazy(() => import("@/pages/ToolsForAirbnbInvestors"));
const RefinanceTrackerForRealEstate = lazy(() => import("@/pages/RefinanceTrackerForRealEstate"));
const ComplianceLandingPage = lazy(() => import("@/pages/ComplianceLandingPage"));
const BankReconciliationLandingPage = lazy(() => import("@/pages/BankReconciliationLandingPage"));
const MaintenanceTeamTasksLandingPage = lazy(() => import("@/pages/MaintenanceTeamTasksLandingPage"));
const AreaIntel = lazy(() => import("@/pages/AreaIntel"));
const RentTrends = lazy(() => import("@/pages/RentTrends"));
const Compliance = lazy(() => import("@/pages/Compliance"));
const References = lazy(() => import("@/pages/References"));
const TenantReference = lazy(() => import("@/pages/TenantReference"));
const TenantLogin = lazy(() => import("@/pages/TenantLogin"));
const TenantPortal = lazy(() => import("@/pages/TenantPortal"));
const RepairForm = lazy(() => import("@/pages/RepairForm"));
const SubmitQuote = lazy(() => import("@/pages/SubmitQuote"));
const Admin = lazy(() => import("@/pages/Admin"));
const NewProject = lazy(() => import("@/pages/NewProject"));
const DevelopmentProjects = lazy(() => import("@/pages/DevelopmentProjects"));
const DevelopmentSites = lazy(() => import("@/pages/DevelopmentSites"));
const DevelopmentPortfolio = lazy(() => import("@/pages/DevelopmentPortfolio"));
const CountryPacks = lazy(() => import("@/pages/CountryPacks"));
const DevelopmentProjectDetail = lazy(() => import("@/pages/DevelopmentProjectDetail"));
const NewDevelopmentProject = lazy(() => import("@/pages/NewDevelopmentProject"));
const PhaseTaskBoard = lazy(() => import("@/pages/PhaseTaskBoard"));
const AutoPayDashboard = lazy(() => import("@/pages/AutoPayDashboard"));
const LandSearch = lazy(() => import("@/pages/LandSearch"));
const DevelopmentFinancialModeling = lazy(() => import("@/pages/FinancialModelingRedirect"));
const Community = lazy(() => import("@/pages/Community"));
const Team = lazy(() => import("@/pages/Team"));
const AcceptInvitation = lazy(() => import("@/pages/AcceptInvitation"));
const SharedAppraisal = lazy(() => import("@/pages/SharedAppraisal"));
const PublicProjectView = lazy(() => import("@/components/brrr/shared/PublicProjectView"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  const handleError = (error: Error, info: React.ErrorInfo) => {
    console.error("Application error:", error, info);
  };

  return (
    <ErrorBoundary onError={handleError}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
            <AuthProvider>
              <Router>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Index />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/shared/appraisal/:token" element={<SharedAppraisal />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/help" element={<HelpCentre />} />
                    <Route path="/glossary" element={<Glossary />} />
                    <Route path="/help-centre" element={<HelpCentre />} />
                    <Route path="/landing" element={<Landing />} />
                    <Route path="/pricing" element={<Pricing />} />

                    {/* Protected routes with AppLayout */}
                    <Route
                      path="/contacts"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Contacts />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/investment-analyser"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <InvestmentAnalyser />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/brrr-deal-calculator-uk"
                      element={<BRRRDealCalculatorUK />}
                    />
                    <Route
                      path="/property-management-tool-for-landlords"
                      element={<PropertyManagementToolForLandlords />}
                    />
                    <Route
                      path="/tools-for-airbnb-investors"
                      element={<ToolsForAirbnbInvestors />}
                    />
                    <Route
                      path="/refinance-tracker-for-real-estate"
                      element={<RefinanceTrackerForRealEstate />}
                    />
                    <Route
                      path="/compliance-landing"
                      element={<ComplianceLandingPage />}
                    />
                    <Route
                      path="/bank-reconciliation-landing"
                      element={<BankReconciliationLandingPage />}
                    />
                    <Route
                      path="/maintenance-team-tasks-landing"
                      element={<MaintenanceTeamTasksLandingPage />}
                    />

                    {/* Tenant routes */}
                    <Route path="/tenant-login" element={<TenantLogin />} />
                    <Route path="/tenant-portal" element={<TenantPortal />} />
                    <Route path="/repair/:propertyId" element={<RepairForm />} />
                    <Route
                      path="/submit-quote/:requestId"
                      element={<SubmitQuote />}
                    />
                    <Route
                      path="/tenant-reference/:referenceId"
                      element={<TenantReference />}
                    />

                    {/* Public Routes */}
                    <Route
                      path="/shared/project/:shareToken"
                      element={<PublicProjectView />}
                    />
                    <Route
                      path="/accept-invitation"
                      element={<AcceptInvitation />}
                    />

                    {/* Protected routes */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Dashboard />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/properties"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Properties />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/properties/:id"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <PropertyDetail />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/add-property"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <AddProperty />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/edit-property/:id"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <EditProperty />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/finance"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Finance />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/financial-report"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <FinancialReport />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/brrr-projects"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <BRRRProjects />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/new-brrr-project"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <NewBRRRProject />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/brrr-projects/:id"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <BRRRProjectDetail />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/calendar"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Calendar />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/country-packs"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <CountryPacks />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Settings />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/user-contacts"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <UserContacts />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/landlords"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Landlords />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/landlords/:id"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <LandlordDetail />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/area-intelligence"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <AreaIntel />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/rent-trends"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <RentTrends />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/compliance"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Compliance />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/references"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <References />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Admin />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/new-project"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <NewProject />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/development-portfolio"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <DevelopmentPortfolio />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/development-sites"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <DevelopmentSites />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/development-projects"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <DevelopmentProjects />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/development-projects/:id"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <DevelopmentProjectDetail />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/new-development-project"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <NewDevelopmentProject />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/development-projects/:projectId/phases/:phaseId/tasks"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <PhaseTaskBoard />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/development-projects/:projectId/financial-modeling"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <DevelopmentFinancialModeling />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/auto-pay"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <AutoPayDashboard />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/deal-search"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <LandSearch />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/community"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Community />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/team"
                      element={
                        <ProtectedRoute>
                          <AppLayout>
                            <Team />
                          </AppLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch all route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </Router>
              <Toaster />
              <ConfirmActionHost />
            </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
